import { ApolloClient } from '@apollo/client';
import { isNotNullPredicate } from '@utils/arrays';
import { shopifyAdminIdToGraphqlId } from '@utils/shopifyIds';

import {
  PRODUCT_TAG_ALLOW_MULTIPLE_ITEMS,
  PRODUCT_TAG_ENQUIRE_ONLY_NO_PRICE,
  PRODUCT_TAG_FREE_SHIPPING,
  PRODUCT_TAG_LIMIT_ONE_PER_COSTUMER,
  PRODUCT_TAG_NO_ORDER_CANCELLATION,
  PRODUCT_TAG_NO_RESALE_AGREEMENT,
  PRODUCT_TAG_NO_TAX_CHARGED,
  PRODUCT_TAG_SOLD_OUT,
} from '../constants/product';
import { CurrencyCode } from '../types/Locations';
import type {
  ProductsGateway,
  ShopifyProductPurchaseDetails,
  ShopifyProductVariant,
} from '../types/Purchasing';
import {
  queryGetProductPurchaseDetails,
  ShopifyGenGetProductPurchaseDetailsQuery,
} from '../types/shopifygen';
import { captureError } from '../utils/capture';
import { tagsToExcludeCountries } from '../utils/product';

export class ShopifyProductsGateway
  implements
    ProductsGateway<
      ShopifyProductIdentifier,
      ShopifyProductPurchaseDetails,
      ShopifyProductVariant
    >
{
  constructor(private client: ApolloClient<any>) {}

  public async getProductPurchaseDetails({
    identifier,
    releaseName,
    variantIdentifier,
  }: {
    identifier: ShopifyProductIdentifier;
    releaseName: string | null;
    variantIdentifier?: string;
  }): Promise<ShopifyProductPurchaseDetails | null> {
    const product = await this.getShopifyProduct(identifier.id);

    if (!product) return null;

    const frames =
      product.collectionSet?.reference?.__typename === 'Collection'
        ? product.collectionSet.reference.products.nodes.flatMap(
            (collectionProduct) => {
              return (
                collectionProduct.frames?.references?.nodes
                  ?.map((frame) => {
                    if (!frame || frame.__typename !== 'Product') {
                      return null;
                    }

                    return {
                      title: frame.frameName?.value ?? '',
                      description: frame.frameFinish?.value ?? '',
                      images: frame.images.nodes.map((image) => ({
                        alt: frame.frameName?.value ?? '',
                        src: image.url,
                      })),
                      shopifyProductId: collectionProduct.id,
                      shopifyFrameId: frame.id,
                      variants: frame.variants.edges.map((variant) => ({
                        title: variant.node.title,
                        price: Number(variant.node.price.amount),
                        shopifyFrameVariantId: variant.node.id,
                      })),
                    };
                  })
                  .filter(isNotNullPredicate) ?? []
              );
            },
          )
        : product.frames?.references?.nodes
            ?.map((frame) => {
              if (!frame || frame.__typename !== 'Product') {
                return null;
              }

              return {
                title: frame.frameName?.value ?? '',
                description: frame.frameFinish?.value ?? '',
                images: frame.images.nodes.map((image) => ({
                  alt: frame.frameName?.value ?? '',
                  src: image.url,
                })),
                shopifyProductId: product.id,
                shopifyFrameId: frame.id,
                variants: frame.variants.edges.map((variant) => ({
                  title: variant.node.title,
                  price: Number(variant.node.price.amount),
                  shopifyFrameVariantId: variant.node.id,
                })),
              };
            })
            .filter(isNotNullPredicate) ?? [];

    const variant = variantIdentifier
      ? getVariantByVariantId(product, variantIdentifier, releaseName)
      : findBestMatchVariant(product, releaseName);

    const collectionSet =
      product.collectionSet?.reference?.__typename === 'Collection'
        ? product.collectionSet.reference.products.nodes
            .map((product) => {
              const productShopifyVariant = product.variants.edges.find(
                (v) => v.node.title === variant.node.title,
              );

              // Collection set should contain only products of chosen variant
              // On product page, it is the public variant
              // On private page, it is the variant selected in the admin panel
              if (!productShopifyVariant) {
                return null;
              }

              // Do not include products out of stock in the collection set
              if (!productShopifyVariant.node.availableForSale) {
                return null;
              }

              return {
                allowMultipleProducts: product.tags.includes(
                  PRODUCT_TAG_ALLOW_MULTIPLE_ITEMS,
                ),
                allowMultipleProductsLimit: product.maximumSalesCapacity?.value
                  ? parseInt(product.maximumSalesCapacity.value, 10)
                  : Number.MAX_SAFE_INTEGER,
                artist: product.artistName?.value ?? '',
                image: product.featuredImage?.url ?? '',
                initial: product.id.endsWith(identifier.id),
                isSoldOut: product.tags.includes(PRODUCT_TAG_SOLD_OUT),
                price: Number(productShopifyVariant.node.price.amount),
                quantity: 1,
                selected: product.id.endsWith(identifier.id),
                shopifyId: product.id,
                shopifyVariantId: productShopifyVariant.node.id,
                title: product.title,
              };
            })
            .filter(isNotNullPredicate)
        : null;

    return {
      shopifyId: product.id,
      name: product.title,
      artist: product.artistName?.value || null,
      slug: product.handle,
      price: Number(variant.node.price.amount),
      currency: product.priceRange.maxVariantPrice.currencyCode as CurrencyCode,
      available: variant.node.availableForSale,
      separateFrameAvailable: frames.length > 0,
      numberOfEditions: Number(product.editionSize?.value ?? 0),
      shopifyVariantId: variant.node.id,
      shopifyVariantTitle: variant.node.title,
      requiresUnlockCode:
        !isPublicVariant(variant) &&
        !isDrawVariant(variant) &&
        !isAuctionVariant(variant),
      allowMultipleProducts: product.tags.includes(
        PRODUCT_TAG_ALLOW_MULTIPLE_ITEMS,
      ),
      noResaleAgreement: product.tags.includes(PRODUCT_TAG_NO_RESALE_AGREEMENT),
      noTaxCharged: product.tags.includes(PRODUCT_TAG_NO_TAX_CHARGED),
      freeShipping: product.tags.includes(PRODUCT_TAG_FREE_SHIPPING),
      noOrderCancellation: product.tags.includes(
        PRODUCT_TAG_NO_ORDER_CANCELLATION,
      ),
      sku: variant.node.sku ?? undefined,
      tags: product.tags,
      closingDate: product.closingDate ? product.closingDate.value : null,
      launchDate: product.launchDate ? product.launchDate.value : null,
      frames,
      shippingRequired: variant.node.requiresShipping,
      limitOnePerCostumer: product.tags.includes(
        PRODUCT_TAG_LIMIT_ONE_PER_COSTUMER,
      ),
      enquireOnlyNoPrice: product.tags.includes(
        PRODUCT_TAG_ENQUIRE_ONLY_NO_PRICE,
      ),
      excludeFromRegions: tagsToExcludeCountries(product.tags),
      framingOffered: product.framingOffered?.value || null,
      framedDispatchDate: product.framedDispatchDate?.value || null,
      unframedDispatchDate: product.unframedDispatchDate?.value || null,
      manifoldListingId: product.manifoldListingId?.value || null,
      auctionFloorPrice: product.auctionFloorPrice?.value || null,
      collectionSet,
      collectoinSetDescription:
        product.collectionSet?.reference?.__typename === 'Collection'
          ? product.collectionSet.reference.worksSelectorDescription?.value ??
            ''
          : null,
      collectoinSetImage:
        product.collectionSet?.reference?.__typename === 'Collection'
          ? product.collectionSet.reference.image?.url ?? null
          : null,
      maximumSalesCapacity: product.maximumSalesCapacity?.value
        ? parseInt(product.maximumSalesCapacity.value, 10)
        : null,
    };
  }

  public async getProductVariants(identifier: ShopifyProductIdentifier) {
    const product = await this.getShopifyProduct(identifier.id);
    if (product === null) {
      return null;
    }

    const variants = product?.variants.edges || [];

    return variants.map<ShopifyProductVariant>((v) => ({
      shopifyVariantId: v.node.id,
      title: v.node.title,
      releaseType: isPublicVariant(v) ? 'public' : 'code',
    }));
  }

  private async getShopifyProduct(
    id: string,
  ): Promise<ShopifyGenGetProductPurchaseDetailsQuery['product']> {
    try {
      const { data } = await queryGetProductPurchaseDetails(this.client, {
        id: shopifyAdminIdToGraphqlId(id),
      });

      return data.product;
    } catch (err) {
      captureError(err);
      return null;
    }
  }
}

const SHOPIFY_DEFAULT_VARIANT_TITLE = 'Default Title';
const PUBLIC_TITLE = 'Public';
const DRAW_TITLE = 'Draw';
const AUCTION_TITLE = 'Auction';

const isAuctionVariant = (v: { node: { title: string } }) =>
  v.node.title === SHOPIFY_DEFAULT_VARIANT_TITLE ||
  v.node.title === AUCTION_TITLE;

const isPublicVariant = (v: { node: { title: string } }) =>
  v.node.title === SHOPIFY_DEFAULT_VARIANT_TITLE ||
  v.node.title === PUBLIC_TITLE;

const isDrawVariant = (v: { node: { title: string } }) =>
  v.node.title === SHOPIFY_DEFAULT_VARIANT_TITLE || v.node.title === DRAW_TITLE;

const findBestMatchVariant = (
  product: NonNullable<ShopifyGenGetProductPurchaseDetailsQuery['product']>,
  releaseName: string | null,
) => {
  const isNamedVariant = (v: { node: { title: string } }) =>
    v.node.title.toLowerCase() === releaseName?.toLowerCase();

  const fallbackVariant = product.variants.edges[0];
  const desiredVariant =
    releaseName === null
      ? product.variants.edges.find(isPublicVariant)
      : product.variants.edges.find(isNamedVariant);

  return desiredVariant || fallbackVariant;
};

const getVariantByVariantId = (
  product: NonNullable<ShopifyGenGetProductPurchaseDetailsQuery['product']>,
  variantId: string,
  releaseName: string | null,
) => {
  const variant = product.variants.edges.find(
    (variant) => variant.node.id === variantId,
  );
  if (!variant) {
    return findBestMatchVariant(product, releaseName);
  }
  return variant;
};
